import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import AnalyticsLayout, { DATE_ALL, DateRange, toToman } from './AnalyticsLayout';

const COLORS = ['#1A6BFF', '#4D8FFF', '#7A8CA0', '#3D4F6B', '#0F1923', '#1254D6', '#93C5FD', '#64748B'];

export default function Users() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>({ from: DATE_ALL, to: DATE_ALL });
  const [data, setData] = useState<any>(null);
  const [sortKey, setSortKey] = useState<'orderCount' | 'totalSpend'>('orderCount');
  const [query, setQuery] = useState('');

  const fetchData = useCallback(async () => {
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.users).get(apiQuery);
    if (res.code === 200) setData(res.data);
    dispatch(setLoading(false));
  }, [dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchData(); }, [fetchData]);

  const allUsers: any[] = data?.allUsers || [];
  const filtered = allUsers.filter(u => {
    const q = query.toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || u.phoneNumber.includes(q) || (u.companyName || '').toLowerCase().includes(q);
  });
  const sorted = [...filtered].sort((a, b) => b[sortKey] - a[sortKey]);

  const exportExcel = () => {
    const rows = sorted.map(u => ({
      'نام': u.name || '—',
      'شماره موبایل': u.phoneNumber,
      'شرکت': u.companyName || '—',
      'تعداد سفارش': u.orderCount,
      'مجموع خرید (تومان)': toToman(u.totalSpend),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'کاربران');
    XLSX.writeFile(wb, 'users-analytics.xlsx');
  };

  const top10ByOrders = [...allUsers].sort((a, b) => b.orderCount - a.orderCount).slice(0, 10).map(u => ({ name: u.name || u.phoneNumber, value: u.orderCount }));
  const top10BySpend = [...allUsers].sort((a, b) => b.totalSpend - a.totalSpend).slice(0, 10).map(u => ({ name: u.name || u.phoneNumber, value: u.totalSpend }));

  return (
    <AnalyticsLayout tab="users" title="تحلیل کاربران" dateRange={dateRange} onDateChange={setDateRange}>

      {/* KPI */}
      <div className="analyticsKpiRow">
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiPurple">👥</div>
          <div className="analyticsKpiValue">{data?.totalUsers ?? 0}</div>
          <div className="analyticsKpiLabel">تعداد کل کاربران</div>
        </div>
        <div className="analyticsKpiCard">
          <div className="analyticsKpiIcon kpiGreen">✅</div>
          <div className="analyticsKpiValue">{data?.activeUsers ?? 0}</div>
          <div className="analyticsKpiLabel">کاربران با سفارش در این بازه</div>
        </div>
      </div>

      {/* Charts */}
      <div className="analyticsChartGrid">
        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">برترین کاربران بر اساس تعداد سفارش</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={top10ByOrders} layout="vertical" margin={{ right: 16, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh' }} />
              <Tooltip formatter={(v: any) => [v, 'سفارش']} labelStyle={{ textAlign: 'right' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} name="تعداد سفارش">
                {top10ByOrders.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="analyticsChartCard">
          <div className="analyticsChartTitle">برترین کاربران بر اساس مجموع خرید</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={top10BySpend} layout="vertical" margin={{ right: 16, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh' }} />
              <Tooltip formatter={(v: any) => [tools.formatPrice(toToman(v)) + ' تومان', 'مجموع خرید']} labelStyle={{ textAlign: 'right' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} name="مجموع خرید">
                {top10BySpend.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="analyticsTableCard">
        <div className="analyticsTableHeader">
          <div className="analyticsChartTitle" style={{ margin: 0 }}>جدول کاربران</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="analyticsExportBtn" onClick={exportExcel}>⬇ خروجی اکسل</button>
            <select
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, direction: 'rtl' }}
              value={sortKey}
              onChange={e => setSortKey(e.target.value as any)}
            >
              <option value="orderCount">مرتب بر اساس: تعداد سفارش</option>
              <option value="totalSpend">مرتب بر اساس: مجموع خرید</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <input
            className="editProductInput"
            style={{ width: 280, marginBottom: 0 }}
            placeholder="جستجو نام، موبایل، شرکت..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <table className="alternating-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>#</th>
              <th>نام</th>
              <th>موبایل</th>
              <th>شرکت</th>
              <th>تعداد سفارش</th>
              <th>مجموع خرید (تومان)</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u, i) => (
              <tr key={u.userId} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                <td>{i + 1}</td>
                <td>{u.name || '—'}</td>
                <td style={{ direction: 'ltr' }}>{u.phoneNumber}</td>
                <td>{u.companyName || '—'}</td>
                <td><strong>{u.orderCount}</strong></td>
                <td>{tools.formatPrice(toToman(u.totalSpend))}</td>
                <td>
                  <button
                    onClick={() => navigate(`/analytics/users/${u.userId}`, { state: { dateRange } })}
                    style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #14337D', color: '#14337D', background: 'transparent', cursor: 'pointer', fontSize: 12 }}
                  >
                    جزئیات
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#aaa' }}>داده‌ای یافت نشد</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AnalyticsLayout>
  );
}
